import React, {useMemo, useState, useCallback, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {isEmpty as _isEmpty} from 'lodash';

import Form from '@appComponents/ReduxFormControls';

const AT_LEAST_ONE_REQUIRED_MESSAGE = 'At least one clip provider must be selected.';
const NO_CLIP_CONFIG_MESSAGE = 'There is no clip configuration for the sport of this event.';

const isAnyClipProviderSelected = (selectionState) =>
    Object.values(selectionState).some((value) => null !== value && value !== undefined && '' !== value);

const ClipProvidersDropdown = ({
    hidden,
    options = [],
    eventContentTypes = [],
    defaultDropdownValue = '',
    defaultDropdownValues = {},
}) => {
    const preselectedClipProviders = useMemo(() => ({
        ...Object.fromEntries(
            eventContentTypes.map((ect) => [ect.id, defaultDropdownValue])
        ),
        ...defaultDropdownValues,
    }), [eventContentTypes, defaultDropdownValue, defaultDropdownValues]);

    const [selectedValues, setSelectedValues] = useState(() =>
        Object.fromEntries(
            eventContentTypes.map((ect) => [ect.id, preselectedClipProviders[ect.id] ?? ''])
        )
    );

    const selectedValuesRef = useRef(selectedValues);

    useEffect(() => {
        selectedValuesRef.current = selectedValues;
    }, [selectedValues]);

    const handleChange = useCallback((ectId) => (_event, value) => {
        const normalizedValue = value ?? '';

        setSelectedValues((prev) => {
            if (prev[ectId] === normalizedValue) {
                return prev;
            }

            const next = {...prev, [ectId]: normalizedValue};
            selectedValuesRef.current = next;

            return next;
        });
    }, []);

    const requiredValidator = useCallback((value, allValues) => {
        if (null !== value && value !== undefined && '' !== value) {
            return undefined;
        }

        return isAnyClipProviderSelected(allValues?.clip_providers ?? {})
            ? undefined
            : AT_LEAST_ONE_REQUIRED_MESSAGE;
    }, []);

    if (_isEmpty(eventContentTypes)) {
        return (
            <div>
                <label htmlFor='clip-config-input'>
                    Clip configuration
                    <input id='clip-config-input' type='hidden' />
                </label>
                {NO_CLIP_CONFIG_MESSAGE}
            </div>
        );
    }

    if (hidden) {
        return null;
    }

    return (
        <>
            {eventContentTypes.map((ect) => (
                <div className='formRow --clipConfigsDropdown' key={ect.id}>
                    <Field
                        component={Form.SemanticSelect}
                        className='--small-width'
                        name={`clip_providers[${ect.id}]`}
                        label={`Clip provider (${ect.name})`}
                        options={options}
                        defaultValue={preselectedClipProviders[ect.id] ?? ''}
                        validate={requiredValidator}
                        onChange={handleChange(ect.id)}
                        allowClear={true}
                    />
                </div>
            ))}
        </>
    );
};

ClipProvidersDropdown.propTypes = {
    hidden: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
    })),
    eventContentTypes: PropTypes.array.isRequired,
    defaultDropdownValue: PropTypes.string,
    defaultDropdownValues: PropTypes.object,
};

export default ClipProvidersDropdown;
